import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../api";
import { Link } from "react-router-dom";
import { Briefcase, Plus, Pencil, Trash2, Search, ArrowLeft, Phone, Mail, MapPin, FileText, ShieldCheck } from "lucide-react";

const PROFESSIONS = [
  { value: "notaire", label: "Notaire" },
  { value: "avocat", label: "Avocat" },
  { value: "courtier", label: "Courtier" },
  { value: "assureur", label: "Assureur" },
  { value: "cgp", label: "CGP" },
  { value: "banquier", label: "Banquier" },
  { value: "expert_comptable", label: "Expert-comptable" },
  { value: "agent_immobilier", label: "Agent immobilier" },
  { value: "mediateur", label: "Médiateur" },
  { value: "geometre", label: "Géomètre" },
  { value: "genealogiste", label: "Généalogiste" },
  { value: "autre", label: "Autre" },
];

const EMPTY = {
  nom: "", prenom: "", profession: "notaire", telephone: "", email: "",
  kbis: "", rcp: "", cabinet: "", adresse_voie: "", code_postal: "",
  ville: "", site_web: "", bio_courte: "", photo_url: "", actif: true,
};

export default function AdminPartenaires() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | partenaire object
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const r = await api.get("/admin/partenaires");
    setItems(r.data.partenaires || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing("new"); setError(""); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditing(p); setError(""); };
  const close = () => { setEditing(null); setForm(EMPTY); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editing === "new") {
        await api.post("/admin/partenaires", form);
      } else {
        await api.put(`/admin/partenaires/${editing.id}`, form);
      }
      await load();
      close();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer ${p.prenom} ${p.nom} de l'annuaire ?`)) return;
    await api.delete(`/admin/partenaires/${p.id}`);
    await load();
  };

  const filtered = items.filter((p) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      p.nom?.toLowerCase().includes(s) ||
      p.prenom?.toLowerCase().includes(s) ||
      p.email?.toLowerCase().includes(s) ||
      p.cabinet?.toLowerCase().includes(s) ||
      p.ville?.toLowerCase().includes(s) ||
      p.profession?.toLowerCase().includes(s)
    );
  });

  return (
    <div className="min-h-screen px-6 md:px-12 py-10" style={{ backgroundColor: "var(--auprea-cream)" }}>
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2" style={{ color: "var(--auprea-blue)", fontSize: 14 }} data-testid="back-to-admin">
            <ArrowLeft size={16} /> Retour au tableau de bord
          </Link>
        </div>

        <div className="flex items-center gap-3 mb-2">
          <Briefcase size={26} style={{ color: "var(--auprea-gold)" }} />
          <div>
            <h1 style={{ fontSize: 32, margin: 0 }}>Annuaire professionnel</h1>
            <p style={{ fontSize: 14, color: "rgba(35,38,46,0.6)", margin: 0 }}>
              {items.length} professionnel{items.length > 1 ? "s" : ""} référencé{items.length > 1 ? "s" : ""}. Visible des forfaits Essentiel et Confort.
            </p>
          </div>
        </div>

        <div className="auprea-card">
          <div className="flex flex-col md:flex-row gap-3 mb-5">
            <div className="flex-1 flex items-center gap-2" style={{ background: "rgba(243,239,230,0.6)", padding: "10px 14px", border: "1px solid rgba(21,41,79,0.1)" }}>
              <Search size={16} style={{ color: "rgba(35,38,46,0.5)" }} />
              <input
                type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher nom, cabinet, ville, profession…"
                className="flex-1 bg-transparent outline-none text-sm"
                data-testid="admin-partenaires-search"
              />
            </div>
            <button onClick={openNew} className="auprea-btn flex items-center gap-2 justify-center" data-testid="admin-partenaire-new">
              <Plus size={16} /> Ajouter un professionnel
            </button>
          </div>

          {filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "rgba(35,38,46,0.5)", fontSize: 14 }}>
              Aucun professionnel pour le moment. Cliquez sur « Ajouter un professionnel » pour commencer.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-partenaires-table">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(21,41,79,0.1)", color: "rgba(35,38,46,0.6)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                    <th className="text-left py-3 pr-4">Nom · Cabinet</th>
                    <th className="text-left py-3 pr-4">Profession</th>
                    <th className="text-left py-3 pr-4">Localisation</th>
                    <th className="text-left py-3 pr-4">Contact</th>
                    <th className="text-left py-3 pr-4">Leads</th>
                    <th className="text-left py-3 pr-4">Statut</th>
                    <th className="text-right py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={p.id} style={{ borderBottom: "1px solid rgba(21,41,79,0.05)" }} data-testid={`row-partenaire-${p.id}`}>
                      <td className="py-3 pr-4">
                        <div style={{ fontWeight: 600, color: "var(--auprea-navy)" }}>{p.prenom} {p.nom}</div>
                        <div style={{ fontSize: 12, color: "rgba(35,38,46,0.6)" }}>{p.cabinet || "—"}</div>
                      </td>
                      <td className="py-3 pr-4">
                        <span style={{ padding: "2px 10px", background: "rgba(202,168,106,0.15)", color: "#8a6f3d", fontSize: 12, borderRadius: 999, fontWeight: 600 }}>
                          {PROFESSIONS.find((pr) => pr.value === p.profession)?.label || p.profession}
                        </span>
                      </td>
                      <td className="py-3 pr-4" style={{ fontSize: 13, color: "rgba(35,38,46,0.75)" }}>
                        {p.code_postal} {p.ville}
                      </td>
                      <td className="py-3 pr-4" style={{ fontSize: 13, color: "rgba(35,38,46,0.75)" }}>
                        <div>{p.telephone}</div>
                        <div style={{ fontSize: 12, color: "rgba(35,38,46,0.55)" }}>{p.email}</div>
                      </td>
                      <td className="py-3 pr-4" style={{ fontFamily: "Spectral, serif", fontSize: 18, color: "var(--auprea-navy)" }}>
                        {p.leads_count || 0}
                      </td>
                      <td className="py-3 pr-4">
                        {p.actif ? (
                          <span style={{ fontSize: 12, color: "var(--auprea-green)", fontWeight: 600 }}>● Actif</span>
                        ) : (
                          <span style={{ fontSize: 12, color: "rgba(35,38,46,0.4)" }}>○ Inactif</span>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        <button onClick={() => openEdit(p)} className="px-2 py-1 mr-1" title="Éditer" data-testid={`edit-${p.id}`}>
                          <Pencil size={15} style={{ color: "var(--auprea-blue)" }} />
                        </button>
                        <button onClick={() => remove(p)} className="px-2 py-1" title="Supprimer" data-testid={`delete-${p.id}`}>
                          <Trash2 size={15} style={{ color: "#b53a3a" }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal édition/création */}
        {editing && (
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(21,41,79,0.6)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, overflowY: "auto" }}
            onClick={close}
            data-testid="partenaire-modal"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ background: "#fff", maxWidth: 720, width: "100%", padding: "32px 36px", borderRadius: 8, maxHeight: "90vh", overflowY: "auto" }}
            >
              <h2 style={{ fontSize: 24, marginBottom: 6 }}>
                {editing === "new" ? "Ajouter un professionnel" : "Modifier le professionnel"}
              </h2>
              <p style={{ fontSize: 13, color: "rgba(35,38,46,0.6)", marginBottom: 22 }}>
                Champs marqués d'un <span style={{ color: "#b53a3a" }}>*</span> obligatoires. Kbis et RCP requis pour la validation de la fiche.
              </p>
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Prénom *" required value={form.prenom} onChange={(v) => setForm({ ...form, prenom: v })} testid="form-prenom" />
                <Field label="Nom *" required value={form.nom} onChange={(v) => setForm({ ...form, nom: v })} testid="form-nom" />
                <div>
                  <label className="auprea-label">Profession *</label>
                  <select className="auprea-input" value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} required data-testid="form-profession">
                    {PROFESSIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <Field label="Cabinet / Étude" value={form.cabinet} onChange={(v) => setForm({ ...form, cabinet: v })} testid="form-cabinet" />
                <Field label="Téléphone mobile *" required value={form.telephone} onChange={(v) => setForm({ ...form, telephone: v })} testid="form-tel" />
                <Field label="Email *" required type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="form-email" />
                <Field label="Numéro Kbis" value={form.kbis} onChange={(v) => setForm({ ...form, kbis: v })} testid="form-kbis" />
                <Field label="Numéro RCP" value={form.rcp} onChange={(v) => setForm({ ...form, rcp: v })} testid="form-rcp" />
                <Field label="Adresse" value={form.adresse_voie} onChange={(v) => setForm({ ...form, adresse_voie: v })} testid="form-adresse" />
                <Field label="Code postal" value={form.code_postal} onChange={(v) => setForm({ ...form, code_postal: v })} testid="form-cp" />
                <Field label="Ville" value={form.ville} onChange={(v) => setForm({ ...form, ville: v })} testid="form-ville" />
                <Field label="Site web (optionnel)" value={form.site_web} onChange={(v) => setForm({ ...form, site_web: v })} testid="form-site" />
                <div className="md:col-span-2">
                  <label className="auprea-label">Bio courte (1-2 phrases visibles sur la fiche)</label>
                  <textarea
                    className="auprea-input" rows={3} value={form.bio_courte}
                    onChange={(e) => setForm({ ...form, bio_courte: e.target.value })}
                    maxLength={300}
                    data-testid="form-bio"
                  />
                </div>
                <div className="md:col-span-2 flex items-center gap-2">
                  <input type="checkbox" id="actif" checked={form.actif} onChange={(e) => setForm({ ...form, actif: e.target.checked })} data-testid="form-actif" />
                  <label htmlFor="actif" style={{ fontSize: 14, color: "var(--auprea-ink)" }}>
                    Fiche active (visible des utilisateurs Essentiel/Confort)
                  </label>
                </div>
                {error && (
                  <div className="md:col-span-2" style={{ color: "#b53a3a", fontSize: 14, padding: "10px 12px", background: "#fcf3f3", border: "1px solid #f0c5c5" }} data-testid="form-error">
                    {error}
                  </div>
                )}
                <div className="md:col-span-2 flex justify-end gap-3 mt-3">
                  <button type="button" onClick={close} className="px-5 py-2" style={{ color: "rgba(35,38,46,0.7)" }} data-testid="form-cancel">
                    Annuler
                  </button>
                  <button type="submit" className="auprea-btn" disabled={saving} data-testid="form-save">
                    {saving ? "Enregistrement…" : (editing === "new" ? "Ajouter à l'annuaire" : "Enregistrer")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", testid }) {
  return (
    <div>
      <label className="auprea-label">{label}</label>
      <input
        type={type} className="auprea-input" value={value || ""}
        onChange={(e) => onChange(e.target.value)} required={required}
        data-testid={testid}
      />
    </div>
  );
}
